import React from 'react';
import { styled } from 'styled-components';
import Price from '@ingka/price';
import PriceModule from '@ingka/price-module';
import Text from '@ingka/text';
import designTokens from '@ingka/variables/design-tokens';
import { Trans, useTranslation } from 'react-i18next';
import { mediaQueries } from '../styles/mediaQueries';
import colors from '../styles/colors';
import {
  getFormattedPrice,
  isIkeaMembershipSavings,
} from '../helpers/currencyHelper';
import {
  type PriceOfferType,
  type CustomerPrice,
} from '../services/appointment/model';
import PayOrderButton from './PayOrderButton';
import type { AppView } from '../types/app';

interface PriceSavingsProps {
  currencyCode: string;
  priceOfferType: PriceOfferType;
  savings: number;
}

interface PayOrderPriceProps {
  price: CustomerPrice;
  currencyCode: string;
}

interface PayOrderProps {
  price: CustomerPrice;
  currencyCode: string;
  payOrderLink: string;
  orderNo: string;
  email?: string;
  page: 'confirm' | 'questions';
  view: AppView;
}

const IkeaMembershipStyled = styled.span<{ $isBusinessNetworkPrice: boolean }>`
  color: ${({ $isBusinessNetworkPrice }) =>
    $isBusinessNetworkPrice
      ? colors.text.default
      : colors.static.ikeaFamilyText};
  font-weight: ${designTokens.fontWeightBold};
`;

const StyledDivider = styled.div`
  visibility: hidden;
  border-top: 1px solid ${colors.neutral.grey200};
  ${mediaQueries.desktopUp} {
    visibility: visible;
    width: 50%;
    margin: 2.5rem 0 0;
    border-top: 2px solid ${colors.neutral.grey200};
  }
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  ${mediaQueries.desktopUp} {
    width: 50%;
  }
  padding-top: 12px;
  padding-bottom: 16px;
  && span {
    margin-inline-end: 0;
  }
`;

const DualPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const getPriceSavingsTextKey = (priceOfferType: string): string =>
  priceOfferType === 'family'
    ? 'pages.confirm.ikeaFamilyMembershipType'
    : 'pages.confirm.ikeaBusinessNetworkMembershipType';

const PriceSavings: React.FC<PriceSavingsProps> = ({
  currencyCode,
  priceOfferType,
  savings,
}) => {
  const { t, i18n } = useTranslation();
  const { mainPrice, secondaryPrice } = getFormattedPrice(
    savings,
    CSS_CONFIG,
    currencyCode,
  );
  const mainIntegerValue = `\u2212${mainPrice.integerValue}`;
  const secondIntegerValue =
    secondaryPrice && `\u2212${secondaryPrice.integerValue}`;
  const hasMembershipSavings = isIkeaMembershipSavings(priceOfferType);

  return (
    <PriceContainer>
      <Text
        tagName="span"
        textStyle="Body.L"
        data-cy={
          hasMembershipSavings
            ? `pay-order-price-discount-label-${priceOfferType}`
            : 'pay-order-price-discount-label'
        }
      >
        {hasMembershipSavings ? (
          <Trans
            i18n={i18n}
            i18nKey={'pages.confirm.totalMembershipSavings'}
            components={{
              membershipType: (
                <IkeaMembershipStyled
                  $isBusinessNetworkPrice={priceOfferType === 'ibn'}
                >
                  {t(getPriceSavingsTextKey(priceOfferType))}
                </IkeaMembershipStyled>
              ),
            }}
          />
        ) : (
          `${t('pages.confirm.totalSavings')}`
        )}
      </Text>
      <DualPriceContainer>
        <Price
          data-cy="pay-order-price-discount"
          {...mainPrice}
          integerValue={mainIntegerValue}
          size="small"
          fixedSize
        />
        {secondIntegerValue && (
          <Price
            data-cy="pay-order-price-discount-second"
            {...secondaryPrice}
            integerValue={secondIntegerValue}
            size="small"
            fixedSize
          />
        )}
      </DualPriceContainer>
    </PriceContainer>
  );
};

export const PayOrderPrice: React.FC<PayOrderPriceProps> = ({
  price,
  currencyCode,
}) => {
  const { t } = useTranslation();
  const {
    totalSavings,
    priceFormatted: { mainPrice, secondaryPrice },
    priceOfferType,
  } = price;
  return (
    <>
      <PriceContainer>
        <Text tagName="span" textStyle="Heading.M">
          {t('pages.confirm.totalPrice')}
        </Text>
        <PriceModule
          productName=""
          currentPriceProps={mainPrice}
          secondaryCurrencyPriceProps={secondaryPrice}
          offerMessageProps={{ children: null }}
          size="medium"
          data-cy="pay-order-price"
        />
      </PriceContainer>
      {priceOfferType && totalSavings && (
        <PriceSavings
          savings={totalSavings}
          currencyCode={currencyCode}
          priceOfferType={priceOfferType}
        />
      )}
    </>
  );
};

const PayOrder: React.FC<PayOrderProps> = ({
  price,
  currencyCode,
  payOrderLink,
  orderNo,
  email,
  page,
  view,
}) => {
  const { t } = useTranslation();
  const showOnlyPrice = view === 'app';

  return showOnlyPrice ? (
    <PayOrderPrice price={price} currencyCode={currencyCode} />
  ) : (
    <>
      <Text tagName="h2" textStyle="Heading.M" data-cy="pay-order">
        {t('pages.confirm.payOrderTitle')}
      </Text>
      <Text tagName="p" textStyle="Body.L">
        {t(`pages.${page}.payOrderDescription`)}
      </Text>
      <StyledDivider />
      <PayOrderPrice price={price} currencyCode={currencyCode} />
      <PayOrderButton
        payOrderLink={payOrderLink}
        orderNo={orderNo}
        email={email}
        buttonText={t('pages.confirm.payOrderButtonText')}
        view={view}
      />
    </>
  );
};

export default PayOrder;
